import React, { useState, useEffect } from 'react';
import { Input, Icon } from '@iq/react-components';
import {
  useFloating,
  useHover,
  useInteractions,
  offset,
  autoUpdate,
  flip,
  shift,
} from '@floating-ui/react';

import Select from '../../CustomSelect';
import './InputWithTooltipField.scss';

/*
 * This component handles a string field with a description and optionally,
 * a boolean toggle that should relate to the string field.
 * The string field can either be a simple input, or if selectOptions
 * are passed in with the schema, a CustomSelect.
 */
const InputWithTooltip = (properties_) => {
  const {
    schema: {
      description,
      parseDescription,
      title,
      selectOptions,
      properties,
      type,
      action,
      placeholder,
      isMulti = false,
    },
    onChange,
    formData,
  } = properties_;

  const [inputToggleKey] =
    Object.entries(properties || {}).find(([, value]) => value.type === 'boolean') || [];
  const [inputKey] =
    Object.entries(properties || {}).find(([, value]) => value.type === 'string') || [];

  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const [data, setData] = useState(formData);

  useEffect(() => {
    onChange(data);
  }, [data]);

  const { x, y, strategy, refs, context } = useFloating({
    open: isTooltipVisible,
    placement: 'right',
    middleware: [offset(15), flip(), shift()],
    onOpenChange: setIsTooltipVisible,
    whileElementsMounted: autoUpdate,
  });

  const handleToggle = () => {
    setData({ ...data, [inputToggleKey]: !data[inputToggleKey] });
  };

  const handleInputChange = (event) => {
    if (type === 'object') {
      setData({ ...data, [inputKey]: event.target.value });
    } else {
      setData(event.target.value);
    }
  };

  const handleSelectChange = (selectedValue) => {
    if (type === 'object') {
      setData({ ...data, [inputKey]: selectedValue });
    } else {
      setData(selectedValue);
    }
  };

  const hover = useHover(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([hover]);

  let theValue = data;
  if (type === 'object') {
    theValue = data?.[inputKey];
  }
  if (type === 'array') {
    theValue = data || [];
  }

  const tooltipContent = parseDescription ? (
    <ul className="description-list">
      {JSON.parse(description).map((item, index) => {
        const itemParts = item.split(':');
        const itemKey = `description-item-${index}`;
        return (
          <li key={itemKey}>
            {itemParts.length === 2 ? (
              <>
                <b>{`${itemParts[0]}: `}</b>
                {itemParts[1]}
              </>
            ) : (
              item
            )}
          </li>
        );
      })}
    </ul>
  ) : (
    description
  );

  return (
    <div className="input-tooltip-wrapper">
      <div className="input-tooltip-top-container">
        <div
          className={`tooltip-container`}
          ref={refs.setReference}
          {...getReferenceProps()}
        >
          <p className="input-title">{title}</p>
          <Icon
            size="s"
            icon={action === 'info' ? 'he-info' : 'he-help'}
            className="input-tooltip-description"
          ></Icon>
          {isTooltipVisible && (
            <div
              className={`tooltip`}
              ref={refs.setFloating}
              style={{
                position: strategy,
                top: y ?? 0,
                left: x ?? 0,
                padding: '1rem',
                display: 'flex',
                width: '40rem',
                zIndex: '200',
              }}
              {...getFloatingProps()}
            >
              {tooltipContent}
            </div>
          )}
        </div>
        {inputToggleKey && (
          <div
            className="input-tooltip-toggle"
            onClick={handleToggle}
          >
            <Icon
              icon={`${data[inputToggleKey] ? 'toggle-on' : 'toggle-off'}`}
              className={`${data[inputToggleKey] ? 'toggle-active' : 'toggle-inactive'}`}
              size="m"
            />
            <p className="toggle-text">{data[inputToggleKey] ? 'Disable' : 'Enable'}</p>
          </div>
        )}
      </div>
      {selectOptions ? (
        <Select
          placeholder={placeholder}
          isMulti={isMulti}
          rawOptions={selectOptions}
          value={theValue}
          onChange={handleSelectChange}
        />
      ) : (
        <Input
          value={theValue}
          onChange={handleInputChange}
        />
      )}
    </div>
  );
};

export default InputWithTooltip;
